/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @from
 * @stompjs/stomp github repository
 * @description
 *
 * @created at Fri Feb 16 2024
 **/

export default class Ticker {
	private _worker?: Worker;
	private _timer?: number;

	constructor(
		private readonly _interval: number,
		private readonly _strategy: "interval" | "worker" = "interval"
	) {}

	public get interval() {
		return this._interval;
	}

	private get _workerScript() {
		return `
    var startTime = Date.now();
    setInterval(function() {
        self.postMessage(Date.now() - startTime);
	}, ${this._interval});
`;
	}

	public start(tick: (elapsedTime: number) => void): void {
		this.stop();

		if (this.shouldUseWorker()) {
			this.runWorker(tick);
		} else {
			this.runInterval(tick);
		}
	}

	public stop(): void {
		this.disposeWorker();
		this.disposeInterval();
	}
	public get timer() {
		return this._timer;
	}

	public get worker() {
		return this._worker;
	}

	public set timer(value: number | undefined) {
		this._timer = value;
	}

	public set worker(value: Worker | undefined) {
		this._worker = value;
	}

	private shouldUseWorker(): boolean {
		return typeof Worker !== "undefined" && this._strategy === "worker";
	}

	private runWorker(tick: (elapsedTime: number) => void): void {
		if (!this._worker) {
			this._worker = new Worker(URL.createObjectURL(new Blob([this._workerScript], { type: "text/javascript" })));
			this._worker.onmessage = (message) => tick(message.data);
		}
	}

	private runInterval(tick: (elapsedTime: number) => void): void {
		if (!this._timer) {
			const startTime = Date.now();
			this._timer = window.setInterval(() => {
				tick(Date.now() - startTime);
			}, this._interval);
		}
	}

	private disposeWorker(): void {
		if (this._worker) {
			this._worker.terminate();
			this._worker = undefined;
		}
	}

	private disposeInterval(): void {
		if (this._timer !== undefined) {
			window.clearInterval(this._timer);
			this._timer = undefined;
		}
	}
}
